import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import MainSidebar from "components/MainSidebar";
import BusinessCard from "./components/BusinessCard";
import RevenueChart from "./components/RevenueChart";
import SectorSummary from "./components/SectorSummary";
import AcquisitionModal from "./components/AcquisitionModal";
import ManageModal from "./components/ManageModal";
import AvailableBusinesses from "./components/AvailableBusinesses";
import TerritoryBonusPanel from "./components/TerritoryBonusPanel";
import LoanModal from "./components/LoanModal";
import { SkeletonCard, EmptyState } from "components/SkeletonLoader";

import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { useRealtimeBusinesses } from "../../hooks/useRealtimeBusinesses";
import { useToast, TOAST_TYPE } from "../../hooks/useToast";
import { logError } from '../../utils/logError';

const BUSINESS_TYPE_LABELS = {
  retail: "Retail",
  tech: "Tech",
  manufacturing: "Manufacturing",
  financial: "Financial",
  black_market: "Black Market",
};

const Businesses = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [ownedBusinesses, setOwnedBusinesses] = useState([]);
  const [playerStats, setPlayerStats] = useState(null);
  const [loans, setLoans] = useState([]);
  const [activeTab, setActiveTab] = useState("owned");
  const [acquisitionTarget, setAcquisitionTarget] = useState(null);
  const [manageTarget, setManageTarget] = useState(null);
  const [sellConfirm, setSellConfirm] = useState(null);
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [realtimeStatus, setRealtimeStatus] = useState('idle');
  const [showLoanModal, setShowLoanModal] = useState(false);
  // Revenue animation state
  const [flashedCards, setFlashedCards] = useState(new Set());
  const [revenueCounters, setRevenueCounters] = useState({});
  const [territories, setTerritories] = useState([]);
  const animFrameRef = useRef({});

  // ── Data Fetching ──────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const [bizRes, statsRes, loansRes] = await Promise.all([
        supabase?.from('businesses')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false }),
        supabase?.from('player_stats')?.select('cash, net_worth, business_skill, level, business_multiplier')?.eq('user_id', user?.id)?.single(),
        supabase?.from('loans')?.select('*')?.eq('user_id', user?.id)?.in('status', ['active', 'overdue'])?.order('created_at', { ascending: false }),
      ]);

      if (bizRes?.error) throw bizRes?.error;
      if (statsRes?.error && statsRes?.error?.code !== 'PGRST116') throw statsRes?.error;

      // Map snake_case → camelCase
      const mapped = (bizRes?.data || [])?.map(b => ({
        id: b?.id,
        name: b?.name,
        sector: b?.business_type,
        status: b?.status,
        level: b?.level,
        revenue: parseFloat(b?.base_revenue || 0),
        operatingCost: parseFloat(b?.operating_cost || 0),
        territoryBonus: parseFloat(b?.territory_bonus || 0),
        competitionIndex: parseFloat(b?.competition_index || 1),
        demandIndex: parseFloat(b?.demand_index || 1),
        territory: b?.territory || 'Uncontrolled',
        employees: b?.employees || 0,
        staffingLevel: parseFloat(b?.staffing_level || 100),
        inventoryLevel: parseFloat(b?.inventory_level || 100),
        securityLevel: parseFloat(b?.security_level || 50),
        estimatedValue: parseFloat(b?.estimated_value || 0),
        purchasePrice: parseFloat(b?.purchase_price || 0),
        upgradeCost: parseFloat(b?.upgrade_cost || 0),
        consecutiveLossTicks: b?.consecutive_loss_ticks || 0,
        lastRevenueAt: b?.last_revenue_at,
      }));

      setOwnedBusinesses(mapped);
      setPlayerStats(statsRes?.data || null);
      setLoans(loansRes?.data || []);
    } catch (err) {
      console.error('Businesses fetch error:', err);
      setError(err?.message || 'Failed to load businesses');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Animated revenue counter helper ──────────────────────
  const animateCounter = useCallback((bizId, targetValue) => {
    if (animFrameRef?.current?.[bizId]) cancelAnimationFrame(animFrameRef?.current?.[bizId]);
    const start = Date.now();
    const duration = 1200;
    const startVal = revenueCounters?.[bizId] || 0;
    const tick = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(startVal + (targetValue - startVal) * eased);
      setRevenueCounters(prev => ({ ...prev, [bizId]: current }));
      if (progress < 1) animFrameRef.current[bizId] = requestAnimationFrame(tick);
    };
    animFrameRef.current[bizId] = requestAnimationFrame(tick);
  }, [revenueCounters]);

  // ── Flash effect on revenue card ─────────────────────────
  const flashCard = useCallback((bizId) => {
    setFlashedCards(prev => new Set([...prev, bizId]));
    setTimeout(() => setFlashedCards(prev => { const n = new Set(prev); n?.delete(bizId); return n; }), 800);
  }, []);

  // ── useRealtimeBusinesses hook ────────────────────────────
  const handleBusinessChange = useCallback((payload) => {
    if (payload?.eventType === 'INSERT') {
      const b = payload?.new;
      const mapped = {
        id: b?.id, name: b?.name, sector: b?.business_type, status: b?.status,
        level: b?.level, revenue: parseFloat(b?.base_revenue || 0),
        operatingCost: parseFloat(b?.operating_cost || 0),
        territoryBonus: parseFloat(b?.territory_bonus || 0),
        competitionIndex: parseFloat(b?.competition_index || 1),
        demandIndex: parseFloat(b?.demand_index || 1),
        territory: b?.territory || 'Uncontrolled', employees: b?.employees || 0,
        staffingLevel: parseFloat(b?.staffing_level || 100),
        inventoryLevel: parseFloat(b?.inventory_level || 100),
        securityLevel: parseFloat(b?.security_level || 50),
        estimatedValue: parseFloat(b?.estimated_value || 0),
        purchasePrice: parseFloat(b?.purchase_price || 0),
        upgradeCost: parseFloat(b?.upgrade_cost || 0),
        consecutiveLossTicks: b?.consecutive_loss_ticks || 0,
      };
      setOwnedBusinesses(prev => [mapped, ...prev?.filter(x => x?.id !== b?.id)]);
    } else if (payload?.eventType === 'UPDATE') {
      const b = payload?.new;
      setOwnedBusinesses(prev => prev?.map(biz =>
        biz?.id === b?.id ? {
          ...biz,
          status: b?.status, level: b?.level,
          revenue: parseFloat(b?.base_revenue || 0),
          operatingCost: parseFloat(b?.operating_cost || 0),
          territoryBonus: parseFloat(b?.territory_bonus || 0),
          estimatedValue: parseFloat(b?.estimated_value || 0),
          upgradeCost: parseFloat(b?.upgrade_cost || 0),
        } : biz
      ));
      // Animate revenue counter on update
      animateCounter(b?.id, parseFloat(b?.base_revenue || 0));
      flashCard(b?.id);
    } else if (payload?.eventType === 'DELETE') {
      setOwnedBusinesses(prev => prev?.filter(biz => biz?.id !== payload?.old?.id));
    }
  }, [animateCounter, flashCard]);

  const handleRevenueEarned = useCallback((log) => {
    const meta = log?.metadata || {};
    const bizName = meta?.business_name || meta?.name || 'Your Business';
    const amount = parseFloat(meta?.amount || meta?.revenue || 0);
    if (amount > 0) {
      showToast(
        `${bizName} earned $${amount?.toLocaleString('en-US', { minimumFractionDigits: 0 })}`,
        TOAST_TYPE?.SUCCESS,
        { duration: 3000 }
      );
    }
    // Flash all cards briefly
    setOwnedBusinesses(prev => { prev?.forEach(b => flashCard(b?.id)); return prev; });
  }, [showToast, flashCard]);

  const handleTerritoryUpdate = useCallback((payload) => {
    if (payload?.new) {
      setTerritories(prev => {
        const existing = prev?.findIndex(t => t?.id === payload?.new?.id);
        if (existing >= 0) { const n = [...prev]; n[existing] = payload?.new; return n; }
        return [...prev, payload?.new];
      });
    }
  }, []);

  const { status: rtBizStatus } = useRealtimeBusinesses({
    userId: user?.id,
    onBusinessChange: handleBusinessChange,
    onRevenueEarned: handleRevenueEarned,
    onTerritoryUpdate: handleTerritoryUpdate,
    enabled: !!user?.id,
  });

  useEffect(() => { setRealtimeStatus(rtBizStatus); }, [rtBizStatus]);

  // ── Computed values ────────────────────────────────────────
  const playerCash = parseFloat(playerStats?.cash || 0);
  const ownedSectors = [...new Set(ownedBusinesses.map(b => b.sector))];
  const totalRevenue = ownedBusinesses?.reduce((s, b) => s + b?.revenue, 0);
  const totalCost = ownedBusinesses?.reduce((s, b) => s + b?.operatingCost, 0);
  const totalNet = totalRevenue - totalCost;
  const totalValue = ownedBusinesses?.reduce((s, b) => s + b?.estimatedValue, 0);
  const totalLoanBalance = loans?.reduce((s, l) => s + parseFloat(l?.remaining_balance || 0), 0);

  const showNotification = (msg, type = "success") => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3500);
  };

  // ── Handlers ──────────────────────────────────────────────
  const handlePurchase = (biz) => setAcquisitionTarget(biz);

  const handleConfirmPurchase = async (biz) => {
    if (!user?.id) return;

    // Validate before purchase
    if (playerCash < biz?.purchasePrice) {
      const needed = (biz?.purchasePrice - playerCash)?.toFixed(2);
      showNotification(`Insufficient funds — need $${parseFloat(needed)?.toLocaleString()} more`, 'error');
      setAcquisitionTarget(null);
      return;
    }
    const alreadyOwned = ownedBusinesses?.some(b => b?.name === biz?.name);
    if (alreadyOwned) {
      showNotification('You already own this business', 'error');
      setAcquisitionTarget(null);
      return;
    }

    setAcquisitionTarget(null);

    // Optimistic UI: immediately add business with pending badge
    const optimisticId = `optimistic-${Date.now()}`;
    const optimisticBiz = {
      id: optimisticId,
      name: biz?.name,
      sector: biz?.sector || biz?.businessType || 'retail',
      status: 'pending',
      level: 1,
      revenue: biz?.revenue || biz?.baseRevenue || 0,
      operatingCost: biz?.operatingCost || 0,
      territoryBonus: 0,
      competitionIndex: 1,
      demandIndex: 1,
      territory: 'Uncontrolled',
      employees: 0,
      staffingLevel: 100,
      inventoryLevel: 100,
      securityLevel: 50,
      estimatedValue: biz?.purchasePrice || 0,
      purchasePrice: biz?.purchasePrice || 0,
      upgradeCost: 0,
      consecutiveLossTicks: 0,
      isPending: true,
    };
    const prevBusinesses = ownedBusinesses;
    const prevCash = playerCash;
    setOwnedBusinesses(prev => [optimisticBiz, ...prev]);
    setPlayerStats(prev => prev ? { ...prev, cash: (parseFloat(prev?.cash || 0) - biz?.purchasePrice) } : prev);

    try {
      const { data, error } = await supabase?.rpc('purchase_business', {
        p_user_id: user?.id,
        p_name: biz?.name,
        p_business_type: biz?.sector || biz?.businessType || 'retail',
        p_purchase_price: biz?.purchasePrice,
        p_base_revenue: biz?.revenue || biz?.baseRevenue || 0,
        p_operating_cost: biz?.operatingCost || 0,
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      showNotification(`${biz?.name} acquired successfully!`, 'success');
      fetchData();
    } catch (err) {
      // Rollback: remove optimistic business, restore cash
      setOwnedBusinesses(prevBusinesses);
      setPlayerStats(prev => prev ? { ...prev, cash: prevCash } : prev);
      showNotification(err?.message || 'Purchase failed', 'error');
      logError({ error: err, componentName: 'Businesses', actionName: 'handleConfirmPurchase', screenName: 'businesses', userId: user?.id, context: { bizName: biz?.name } });
    }
  };

  const handleManageSave = async (updated) => {
    if (!user?.id) return;
    try {
      const { error } = await supabase?.from('businesses')?.update({
          staffing_level: updated?.staffingLevel,
          inventory_level: updated?.inventoryLevel,
          security_level: updated?.securityLevel,
          updated_at: new Date()?.toISOString(),
        })?.eq('id', updated?.id)?.eq('user_id', user?.id);
      if (error) throw error;
      setOwnedBusinesses(prev => prev?.map(b => b?.id === updated?.id ? updated : b));
      showNotification("Business settings updated.", "success");
    } catch (err) {
      showNotification(err?.message || 'Update failed', 'error');
    }
  };

  const handleUpgrade = async (biz) => {
    if (playerCash < biz?.upgradeCost) {
      showNotification("Insufficient funds for upgrade.", "error");
      return;
    }
    try {
      const { error } = await supabase?.from('businesses')?.update({ status: 'upgrading', level: biz?.level + 1, updated_at: new Date()?.toISOString() })?.eq('id', biz?.id)?.eq('user_id', user?.id);
      if (error) throw error;
      await supabase?.from('player_stats')?.update({ cash: playerCash - biz?.upgradeCost, updated_at: new Date()?.toISOString() })?.eq('user_id', user?.id);
      showNotification(`${biz?.name} upgrade initiated!`, "success");
      fetchData();
    } catch (err) {
      showNotification(err?.message || 'Upgrade failed', 'error');
    }
  };

  const handleSell = (biz) => setSellConfirm(biz);

  const confirmSell = async () => {
    if (!sellConfirm || !user?.id) return;
    try {
      const { error } = await supabase?.from('businesses')?.delete()?.eq('id', sellConfirm?.id)?.eq('user_id', user?.id);
      if (error) throw error;
      await supabase?.from('player_stats')?.update({ cash: playerCash + sellConfirm?.estimatedValue, updated_at: new Date()?.toISOString() })?.eq('user_id', user?.id);
      showNotification(`${sellConfirm?.name} sold for $${sellConfirm?.estimatedValue?.toLocaleString()}`, "success");
      setSellConfirm(null);
      fetchData();
    } catch (err) {
      showNotification(err?.message || 'Sale failed', 'error');
    }
  };

  const tabs = [
    { id: "owned", label: "My Businesses", icon: "Building2", count: ownedBusinesses?.length },
    { id: "acquire", label: "Acquire", icon: "Store", count: null },
    { id: "analytics", label: "Analytics", icon: "BarChart2", count: null },
    { id: "territory", label: "Territory", icon: "MapPin", count: null },
    { id: "loans", label: "Loans", icon: "CreditCard", count: loans?.length > 0 ? loans?.length : null },
  ];

  return (
    <div className="min-h-screen flex" style={{ background: "var(--color-background)" }}>
      <MainSidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(p => !p)}
        userRole="player"
        notificationCounts={{ notifications: 4, wars: 2, "crime-hub": 1 }}
        userStats={{
          username: user?.email?.split('@')?.[0] || 'Player',
          level: playerStats?.level || 1,
          energy: 100, maxEnergy: 100,
          xp: 0, xpToNext: 1000,
        }}
      />
      <div
        className="flex-1 flex flex-col min-h-screen transition-all duration-250"
        style={{ marginLeft: sidebarCollapsed ? "72px" : "280px" }}
      >
        {/* Top Bar */}
        <header className="sticky top-0 z-10 flex items-center justify-between px-4 md:px-6 lg:px-8 h-16 border-b" style={{ background: "var(--color-secondary)", borderColor: "var(--color-border)" }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(255,184,0,0.1)", border: "1px solid rgba(255,184,0,0.2)" }}>
              <Icon name="Building2" size={16} color="var(--color-accent)" />
            </div>
            <div>
              <h1 className="font-heading text-sm md:text-base font-bold text-foreground">Business Empire</h1>
              <p className="font-caption text-xs text-muted-foreground hidden md:block">Manage your commercial enterprises</p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            {/* Live badge */}
            <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full border" style={{
              background: realtimeStatus === 'connected' ? 'rgba(16,185,129,0.1)' : 'rgba(255,255,255,0.04)',
              borderColor: realtimeStatus === 'connected' ? 'rgba(16,185,129,0.3)' : 'var(--color-border)',
            }}>
              <div
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  background: realtimeStatus === 'connected' ? 'var(--color-success)' : realtimeStatus === 'error' ? 'var(--color-error)' : 'var(--color-muted-foreground)',
                  boxShadow: realtimeStatus === 'connected' ? '0 0 6px rgba(16,185,129,0.8)' : 'none',
                  animation: realtimeStatus === 'connected' ? 'pulse 2s infinite' : 'none',
                }}
              />
              <span className="font-caption text-xs font-medium" style={{ color: realtimeStatus === 'connected' ? 'var(--color-success)' : 'var(--color-muted-foreground)' }}>
                {realtimeStatus === 'connected' ? 'Live' : realtimeStatus === 'error' ? 'Reconnecting' : 'Connecting'}
              </span>
            </div>
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg border" style={{ background: "rgba(16,185,129,0.08)", borderColor: "rgba(16,185,129,0.2)" }}>
              <Icon name="Wallet" size={13} color="var(--color-success)" />
              <span className="font-heading text-xs font-semibold" style={{ color: "var(--color-success)" }}>${playerCash?.toLocaleString()}</span>
            </div>
            <Button variant="outline" size="sm" iconName="BarChart2" iconPosition="left" onClick={() => navigate("/stock-exchange")}>
              <span className="hidden sm:inline">Exchange</span>
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate("/crime-hub")}>
              <Icon name="Skull" size={16} color="var(--color-muted-foreground)" />
            </Button>
          </div>
        </header>

        {/* Notification Toast */}
        {notification && (
          <div className="fixed top-20 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl border shadow-lg transition-all duration-300" style={{ background: notification?.type === "success" ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.15)", borderColor: notification?.type === "success" ? "rgba(16,185,129,0.3)" : "rgba(239,68,68,0.3)" }}>
            <Icon name={notification?.type === "success" ? "CheckCircle" : "AlertCircle"} size={14} color={notification?.type === "success" ? "var(--color-success)" : "var(--color-error)"} />
            <span className="font-caption text-xs text-foreground">{notification?.msg}</span>
          </div>
        )}

        <main className="flex-1 px-4 md:px-6 lg:px-8 py-6 pb-20 lg:pb-6">
          {/* Error state */}
          {error && (
            <div className="mb-4 p-3 rounded-xl border flex items-center gap-2" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.2)" }}>
              <Icon name="AlertCircle" size={14} color="var(--color-error)" />
              <span className="font-caption text-xs" style={{ color: "var(--color-error)" }}>{error}</span>
              <button onClick={fetchData} className="ml-auto font-caption text-xs underline" style={{ color: "var(--color-primary)" }}>Retry</button>
            </div>
          )}

          {/* KPI Strip */}
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {[0,1,2,3]?.map(i => (
                <div key={i} className="rounded-xl border p-4 animate-pulse" style={{ background: "var(--color-card)", borderColor: "var(--color-border)" }}>
                  <div className="h-3 w-20 rounded mb-2" style={{ background: "rgba(255,255,255,0.06)" }} />
                  <div className="h-6 w-24 rounded" style={{ background: "rgba(255,255,255,0.06)" }} />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {[
                { label: "Total Revenue", value: `$${totalRevenue?.toLocaleString()}`, sub: "per tick", icon: "TrendingUp", color: "var(--color-success)", bizId: 'revenue' },
                { label: "Operating Costs", value: `$${totalCost?.toLocaleString()}`, sub: "per tick", icon: "TrendingDown", color: "var(--color-error)", bizId: 'cost' },
                { label: "Net Income", value: `$${totalNet?.toLocaleString()}`, sub: "per tick", icon: "DollarSign", color: totalNet >= 0 ? "var(--color-success)" : "var(--color-error)", bizId: 'net' },
                { label: "Portfolio Value", value: `$${(totalValue / 1000000)?.toFixed(2)}M`, sub: `${ownedBusinesses?.length} businesses`, icon: "Briefcase", color: "var(--color-primary)", bizId: 'portfolio' },
              ]?.map(kpi => (
                <div
                  key={kpi?.label}
                  className="rounded-xl border p-3 md:p-4 transition-all duration-300"
                  style={{
                    background: flashedCards?.has(kpi?.bizId) ? `${kpi?.color}18` : "var(--color-card)",
                    borderColor: flashedCards?.has(kpi?.bizId) ? `${kpi?.color}50` : "var(--color-border)",
                    boxShadow: flashedCards?.has(kpi?.bizId) ? `0 0 16px ${kpi?.color}30` : 'none',
                    transition: 'background 0.3s, border-color 0.3s, box-shadow 0.3s',
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-caption text-xs text-muted-foreground">{kpi?.label}</span>
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${kpi?.color}15` }}>
                      <Icon name={kpi?.icon} size={13} color={kpi?.color} />
                    </div>
                  </div>
                  <div className="font-heading text-base md:text-lg font-bold" style={{ color: kpi?.color }}>{kpi?.value}</div>
                  <div className="font-caption text-xs text-muted-foreground mt-0.5">{kpi?.sub}</div>
                </div>
              ))}
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 mb-6 overflow-x-auto pb-1">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg font-caption text-xs font-medium transition-all duration-200 whitespace-nowrap flex-shrink-0"
                style={{
                  background: activeTab === tab?.id ? "rgba(0,217,255,0.12)" : "transparent",
                  color: activeTab === tab?.id ? "var(--color-primary)" : "var(--color-muted-foreground)",
                  border: `1px solid ${activeTab === tab?.id ? "rgba(0,217,255,0.25)" : "transparent"}`,
                }}
              >
                <Icon name={tab?.icon} size={13} color={activeTab === tab?.id ? "var(--color-primary)" : "var(--color-muted-foreground)"} />
                {tab?.label}
                {tab?.count !== null && (
                  <span className="px-1.5 py-0.5 rounded-full text-xs" style={{ background: activeTab === tab?.id ? "rgba(0,217,255,0.2)" : "rgba(255,255,255,0.08)", color: activeTab === tab?.id ? "var(--color-primary)" : "var(--color-muted-foreground)" }}>
                    {tab?.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === "owned" && (
            <div className="space-y-4">
              {loading ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {[0,1,2,3]?.map(i => <SkeletonCard key={i} />)}
                </div>
              ) : ownedBusinesses?.length === 0 ? (
                <EmptyState
                  icon="Building2"
                  title="No businesses owned"
                  description="Purchase your first business to start earning passive income and building your empire."
                  action={() => setActiveTab('acquire')}
                  actionLabel="Browse Acquisitions"
                />
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {ownedBusinesses?.map(biz => (
                    <BusinessCard
                      key={biz?.id}
                      business={biz}
                      onManage={setManageTarget}
                      onUpgrade={handleUpgrade}
                      onSell={handleSell}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "acquire" && (
            <AvailableBusinesses playerCash={playerCash} onPurchase={handlePurchase} />
          )}

          {activeTab === "analytics" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2">
                <RevenueChart ownedSectors={ownedSectors} />
              </div>
              <div>
                <SectorSummary businesses={ownedBusinesses} />
              </div>
            </div>
          )}

          {activeTab === "territory" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <TerritoryBonusPanel ownedSectors={ownedSectors} />
              <div className="rounded-xl border p-4 md:p-5" style={{ background: "var(--color-card)", borderColor: "var(--color-border)" }}>
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Swords" size={16} color="var(--color-error)" />
                  <h3 className="font-heading text-sm font-semibold text-foreground">Territory Control Tips</h3>
                </div>
                <div className="space-y-3">
                  {[
                    { icon: "Users", color: "var(--color-primary)", title: "Join a Faction", desc: "Factions compete for territory control. Join one to benefit from collective territory bonuses." },
                    { icon: "Swords", color: "var(--color-error)", title: "Participate in Wars", desc: "Territory wars determine which faction controls each district and its associated revenue bonuses." },
                    { icon: "MapPin", color: "var(--color-accent)", title: "Strategic Location", desc: "Purchase businesses in territories your faction controls to maximize revenue multipliers." },
                    { icon: "Shield", color: "var(--color-success)", title: "Defend Territory", desc: "Maintain faction strength to defend controlled territories from rival faction attacks." },
                  ]?.map(tip => (
                    <div key={tip?.title} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: "rgba(255,255,255,0.02)" }}>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${tip?.color}15` }}>
                        <Icon name={tip?.icon} size={14} color={tip?.color} />
                      </div>
                      <div>
                        <div className="font-caption text-xs font-semibold text-foreground mb-0.5">{tip?.title}</div>
                        <p className="font-caption text-xs text-muted-foreground">{tip?.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "loans" && (
            <div className="space-y-4">
              {/* Loan summary */}
              <div className="rounded-xl border p-4" style={{ background: "var(--color-card)", borderColor: "var(--color-border)" }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon name="CreditCard" size={16} color="var(--color-accent)" />
                    <h3 className="font-heading text-sm font-semibold text-foreground">Active Loans</h3>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-caption text-xs text-muted-foreground">Total owed: <span style={{ color: "var(--color-error)" }}>${totalLoanBalance?.toLocaleString()}</span></span>
                    <button
                      onClick={() => setShowLoanModal(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary border border-primary/20 text-xs font-semibold hover:bg-primary hover:text-zinc-950 transition-all"
                    >
                      <Icon name="Plus" size={12} />
                      {loans?.length === 0 ? 'Take Loan' : 'Manage Loan'}
                    </button>
                  </div>
                </div>
                {loans?.length === 0 ? (
                  <div className="text-center py-8">
                    <Icon name="CreditCard" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                    <p className="font-caption text-sm text-muted-foreground mb-1">No active loans</p>
                    <p className="font-caption text-xs text-muted-foreground">Take a loan to fund business acquisitions and expansions.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {loans?.map(loan => (
                      <div key={loan?.id} className="flex items-center justify-between p-3 rounded-lg border" style={{ background: "rgba(255,255,255,0.02)", borderColor: "var(--color-border)" }}>
                        <div>
                          <div className="font-caption text-xs font-semibold text-foreground">
                            ${parseFloat(loan?.remaining_balance)?.toLocaleString()} remaining
                          </div>
                          <div className="font-caption text-xs text-muted-foreground">
                            {(parseFloat(loan?.interest_rate) * 100)?.toFixed(1)}% interest · Due {new Date(loan?.due_at)?.toLocaleDateString()}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className="px-2 py-0.5 rounded-full font-caption text-xs"
                            style={{
                              background: loan?.status === 'overdue' ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.1)',
                              color: loan?.status === 'overdue' ? 'var(--color-error)' : 'var(--color-success)',
                            }}
                          >
                            {loan?.status}
                          </span>
                          <button
                            onClick={() => setShowLoanModal(true)}
                            className="text-xs text-primary hover:underline"
                          >
                            Repay
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
      {/* Modals */}
      {acquisitionTarget && (
        <AcquisitionModal
          business={acquisitionTarget}
          playerCash={playerCash}
          onConfirm={handleConfirmPurchase}
          onCancel={() => setAcquisitionTarget(null)}
          onClose={() => setAcquisitionTarget(null)}
        />
      )}
      {manageTarget && (
        <ManageModal
          business={manageTarget}
          onSave={handleManageSave}
          onClose={() => setManageTarget(null)}
        />
      )}
      {sellConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.7)" }}>
          <div className="rounded-2xl border p-6 max-w-sm w-full mx-4" style={{ background: "var(--color-card)", borderColor: "var(--color-border)" }}>
            <h3 className="font-heading text-base font-bold text-foreground mb-2">Confirm Sale</h3>
            <p className="font-caption text-sm text-muted-foreground mb-4">
              Sell <strong>{sellConfirm?.name}</strong> for <strong style={{ color: "var(--color-success)" }}>${sellConfirm?.estimatedValue?.toLocaleString()}</strong>?
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setSellConfirm(null)}>Cancel</Button>
              <Button variant="default" className="flex-1" onClick={confirmSell}>Confirm Sale</Button>
            </div>
          </div>
        </div>
      )}
      {showLoanModal && (
        <LoanModal
          playerCash={playerCash}
          ownedBusinesses={ownedBusinesses}
          activeLoan={loans?.[0] || null}
          onClose={() => setShowLoanModal(false)}
          onSuccess={() => { fetchData(); setShowLoanModal(false); }}
        />
      )}
    </div>
  );
};

export default Businesses;